import { setPositions } from '@moleculeone/draw';
import { Mol } from '@moleculeone/types';
import { pick } from 'lodash';
import { useEffect, useState } from 'react';

import { SVGMol } from '../components/visualization/Compound/SVGMol';
import { smilesToMol } from '../utils/conversion';
import { calculateSeededPositions } from '../utils/mol';

const MOL_CACHE: Record<string, Mol> = {};

type UseSmilesToMolOptions = {
  enabled?: boolean;
  smiles: string[];
  delay?: number;
  shouldCalculateAtomPositions?: boolean;
};

function calculateAtomPositions(prevMol: Mol, smiles: string) {
  if (!prevMol) {
    return null;
  }

  const nextMol = setPositions(
    prevMol,
    calculateSeededPositions({
      mol: prevMol,
      seedId: smiles,
    }),
  );

  return nextMol;
}

async function buildMol(
  smiles: string,
  { delay = 0, abortSignal = null }: { delay: number; abortSignal: AbortSignal | null },
): Promise<Mol> {
  return new Promise((resolve, reject) => {
    if (MOL_CACHE[smiles]) {
      resolve(MOL_CACHE[smiles]);
    }

    setTimeout(async () => {
      if (abortSignal) {
        if (abortSignal.aborted) {
          reject(new DOMException('Aborted', 'AbortError'));
        }

        abortSignal.addEventListener('abort', () => reject(new DOMException('Aborted', 'AbortError')));
      }

      const result = await smilesToMol(smiles);

      MOL_CACHE[smiles] = result;
      resolve(result);
    }, delay);
  });
}

export function useSmilesToMol(options: UseSmilesToMolOptions) {
  const [mols, setMols] = useState<Mol[]>();
  const [loading, setLoading] = useState(false);

  const { enabled = true, smiles, delay = 0, shouldCalculateAtomPositions = true } = options;

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (smiles.length === 0) {
      return;
    }

    const abortController = new AbortController();
    const abortSignal = abortController.signal;

    async function run() {
      try {
        setLoading(true);
        let mols = await Promise.all(
          smiles.map(smiles =>
            buildMol(smiles, {
              delay,
              abortSignal,
            }),
          ),
        );

        if (shouldCalculateAtomPositions) {
          mols = mols.map((mol, idx) => calculateAtomPositions(mol, smiles[idx]));
        }

        if (!abortSignal.aborted) {
          setMols(mols);
        }
      } catch (error) {
        if (error instanceof Error && error.name !== 'AbortError') {
          throw error;
        }
      } finally {
        setLoading(false);
      }
    }

    run();

    return () => abortController.abort();
  }, [JSON.stringify(smiles), JSON.stringify(pick(SVGMol.randomSeeds, smiles))]);

  return { mols, loading };
}
